import { useState } from 'react';
import { Box, List, ListItem, Slide, Typography, useMediaQuery, useTheme } from '@mui/material';

import useLocalization from '../../../../hooks/useLocalization';
import { getMobileAnchoredPanelSx, styles } from '../styles';
import type { PreMatchWidgetData, PreMatchWidgetLineupTeam, TeamSide } from '../types';
import TeamVisual from './TeamVisual';

import { mergeSx } from '../../../../utils/styles';

const MOBILE_LINEUP_TEAMS: TeamSide[] = ['home', 'away'];

type LineupTeamCardProps = {
  side: TeamSide;
  lineup: PreMatchWidgetLineupTeam;
  sideAligned?: boolean;
  startersLabel: string;
  substitutesLabel: string;
  coachLabel: string;
  emptySectionMessage: string;
};
const LineupSection = ({
  side,
  title,
  players,
  emptyMessage,
}: {
  side: TeamSide;
  title: string;
  players: string[];
  emptyMessage: string;
}) => (
  <Box sx={mergeSx(styles.lineupSection, side === 'away' && styles.mobileAnchoredListGroupRight)}>
    <Typography sx={mergeSx(styles.lineupSectionTitle, side === 'away' && styles.mobileAnchoredTextBlockRight)}>
      {title}
    </Typography>
    {players.length ? (
      <List sx={styles.playerList}>
        {players.map((player) => (
          <ListItem
            key={`${title}-${player}`}
            sx={mergeSx(styles.playerItem, side === 'away' && styles.mobileAnchoredListItemRight)}
          >
            <Typography sx={mergeSx(styles.playerName, side === 'away' && styles.mobileAnchoredTextBlockRight)}>
              {player}
            </Typography>
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography sx={mergeSx(styles.lineupEmptyLabel, side === 'away' && styles.mobileAnchoredTextRight)}>
        {emptyMessage}
      </Typography>
    )}
  </Box>
);

const LineupTeamCard = ({
  side,
  lineup,
  sideAligned = false,
  startersLabel,
  substitutesLabel,
  coachLabel,
  emptySectionMessage,
}: LineupTeamCardProps) => (
  <Box sx={mergeSx(styles.lineupTeamCard, ...(sideAligned ? getMobileAnchoredPanelSx() : []))}>
    <Box sx={styles.lineupSections}>
      <LineupSection side={side} title={startersLabel} players={lineup.starters} emptyMessage={emptySectionMessage} />
      <LineupSection
        side={side}
        title={substitutesLabel}
        players={lineup.substitutes}
        emptyMessage={emptySectionMessage}
      />
      <Box sx={mergeSx(styles.lineupSection, side === 'away' && styles.mobileAnchoredListGroupRight)}>
        <Typography sx={mergeSx(styles.lineupSectionTitle, side === 'away' && styles.mobileAnchoredTextBlockRight)}>
          {coachLabel}
        </Typography>
        <Typography sx={mergeSx(styles.lineupStaffLabel, side === 'away' && styles.mobileAnchoredTextBlockRight)}>
          {lineup.coach?.name ?? emptySectionMessage}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const LineupsPanel = ({ data }: { data: PreMatchWidgetData }) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTeam, setActiveTeam] = useState<TeamSide>('home');
  const emptySectionMessage = t('preMatchWidget.lineups.emptySection');
  const startersLabel = t('preMatchWidget.lineups.starters');
  const substitutesLabel = t('preMatchWidget.lineups.substitutes');
  const coachLabel = t('preMatchWidget.lineups.coach');
  const refereeLabel = t('preMatchWidget.lineups.referee');

  if (!data.lineups?.home || !data.lineups?.away || !data.lineups?.referees) {
    return <Typography sx={styles.summaryDescription}>{t('preMatchWidget.lineups.empty')}</Typography>;
  }

  const homeLineup = data.lineups.home;
  const awayLineup = data.lineups.away;
  const referees = data.lineups.referees;
  const hasAnyLineupData =
    homeLineup.starters.length ||
    homeLineup.substitutes.length ||
    awayLineup.starters.length ||
    awayLineup.substitutes.length ||
    homeLineup.coach ||
    awayLineup.coach ||
    referees.length;

  if (!hasAnyLineupData) {
    return <Typography sx={styles.summaryDescription}>{t('preMatchWidget.lineups.empty')}</Typography>;
  }

  const refereeText = referees.length
    ? referees
        .map((referee) =>
          referee.role && referee.role !== 'referee' ? `${referee.name} (${referee.role})` : referee.name
        )
        .join(', ')
    : emptySectionMessage;
  const activeLineup = activeTeam === 'home' ? homeLineup : awayLineup;

  return (
    <Box>
      <Typography sx={styles.panelHeader}>{t('preMatchWidget.lineups.title')}</Typography>
      {isMobile ? (
        <Box sx={styles.lineupsShell}>
          <Box sx={styles.mobileLineupSelector} role='tablist' aria-label={t('preMatchWidget.aria.lineupTablist')}>
            {MOBILE_LINEUP_TEAMS.map((side) => {
              const isActive = side === activeTeam;
              const team = data.fixture[side];

              return (
                <Box
                  key={side}
                  component='button'
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  onClick={() => setActiveTeam(side)}
                  sx={mergeSx(
                    styles.mobileLineupSelectorButton,
                    side === 'home' ? styles.mobileLineupSelectorButtonLeft : styles.mobileLineupSelectorButtonRight
                  )}
                >
                  <TeamVisual team={team} sx={styles.mobileTabTeamVisual} />
                  <Typography
                    sx={mergeSx(
                      styles.mobileLineupSelectorLabel,
                      isActive && styles.mobileLineupSelectorLabelActive,
                      side === 'away' && styles.mobileLineupSelectorLabelRight
                    )}
                  >
                    {team.shortName ?? team.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={styles.mobileLineupSelectorIndicator} aria-hidden='true'>
            {MOBILE_LINEUP_TEAMS.map((side) => (
              <Box
                key={side}
                sx={mergeSx(
                  styles.mobileLineupSelectorIndicatorSegment,
                  side === activeTeam && styles.mobileLineupSelectorIndicatorSegmentActive
                )}
              />
            ))}
          </Box>
          <Box sx={styles.mobileLineupStage} data-testid='mobile-lineup-panel' data-team-side={activeTeam}>
            <Slide
              key={activeTeam}
              in
              direction={activeTeam === 'home' ? 'right' : 'left'}
              mountOnEnter
              unmountOnExit
              appear={false}
              timeout={250}
            >
              <Box sx={styles.mobileLineupSlide}>
                <LineupTeamCard
                  side={activeTeam}
                  lineup={activeLineup}
                  sideAligned
                  startersLabel={startersLabel}
                  substitutesLabel={substitutesLabel}
                  coachLabel={coachLabel}
                  emptySectionMessage={emptySectionMessage}
                />
              </Box>
            </Slide>
          </Box>
          <Box sx={styles.lineupsRefereeCard}>
            <Typography sx={styles.lineupsRefereeLabel}>{refereeLabel}</Typography>
            <Typography sx={styles.lineupsRefereeValue}>{refereeText}</Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={styles.lineupsShell}>
          <Box sx={styles.lineupsDesktopHeader}>
            <Box sx={styles.lineupsTeamSummary}>
              <TeamVisual team={data.fixture.home} sx={styles.lineupsSummaryTeamVisual} />
              <Typography sx={styles.lineupsSummaryTeamLabel}>{data.fixture.home.name}</Typography>
            </Box>
            <Box sx={styles.lineupsRefereeCard}>
              <Typography sx={styles.lineupsRefereeLabel}>{refereeLabel}</Typography>
              <Typography sx={styles.lineupsRefereeValue}>{refereeText}</Typography>
            </Box>
            <Box sx={styles.lineupsTeamSummary}>
              <TeamVisual team={data.fixture.away} sx={styles.lineupsSummaryTeamVisual} />
              <Typography sx={styles.lineupsSummaryTeamLabel}>{data.fixture.away.name}</Typography>
            </Box>
          </Box>
          <Box sx={styles.lineupsPanels}>
            <LineupTeamCard
              side='home'
              lineup={homeLineup}
              startersLabel={startersLabel}
              substitutesLabel={substitutesLabel}
              coachLabel={coachLabel}
              emptySectionMessage={emptySectionMessage}
            />
            <LineupTeamCard
              side='away'
              lineup={awayLineup}
              startersLabel={startersLabel}
              substitutesLabel={substitutesLabel}
              coachLabel={coachLabel}
              emptySectionMessage={emptySectionMessage}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LineupsPanel;
